import {
  FocusEvent,
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import * as Primitive from '@radix-ui/react-select';
import composeRefs from '@seznam/compose-react-refs';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import connectFieldsStyles from '@components/primitives/ConnectedFields/styles.module.css';
import { useFormField } from '@components/primitives/FormField';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import { SelectLabel } from './SelectLabel';
import SelectOption from './SelectOption';

import css from './styles.module.css';

export type SelectProps<T extends string> = PropsWithChildren & {
  error?: boolean;
  id?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  prefix?: React.ReactNode;
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  align?: 'start' | 'center' | 'end';
  value?: T;
  label?: string;
  onChange?: (value: T) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  inline?: boolean;
  trigger?: ReactNode;
  autoFocus?: boolean;
};

const Select = forwardRef<HTMLButtonElement, SelectProps<string>>(
  function Select(
    {
      id,
      children,
      placeholder,
      prefix,
      error,
      size = 'md',
      align = 'center',
      value,
      onChange,
      onBlur,
      name,
      label,
      className,
      disabled,
      inline,
      trigger,
      autoFocus,
    },
    ref
  ) {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const formField = useFormField();

    const openRef = useRef(false);

    const hasError = error || Boolean(formField?.error);

    const handleBlur = useCallback(
      (event: FocusEvent<HTMLButtonElement>) => {
        if (openRef.current) return;
        onBlur?.(event);
      },
      [onBlur]
    );

    return (
      <Primitive.Root
        value={value}
        onValueChange={onChange}
        name={name}
        disabled={disabled}
        onOpenChange={(open) => (openRef.current = open)}
      >
        {trigger ? (
          <Primitive.Trigger
            ref={composeRefs(ref, triggerRef)}
            onBlur={handleBlur}
            autoFocus={autoFocus}
            asChild
          >
            {trigger}
          </Primitive.Trigger>
        ) : (
          <Primitive.Trigger
            ref={composeRefs(ref, triggerRef)}
            id={id || formField?.id}
            className={cn(
              css.select,
              connectFieldsStyles.connectableField,
              className
            )}
            data-error={hasError}
            data-size={size}
            data-inline={inline}
            aria-label={label}
            autoFocus={autoFocus}
            onBlur={handleBlur}
          >
            {prefix && (
              <span className={css.prefix} aria-hidden>
                {prefix}
              </span>
            )}
            <span className={css.value}>
              <Primitive.Value placeholder={placeholder || 'Select...'} />
            </span>
            <Primitive.Icon className={css.icon} aria-hidden>
              <ChevronsUpDown />
            </Primitive.Icon>
          </Primitive.Trigger>
        )}

        <AnimatePresence initial={false}>
          <Primitive.Portal
            container={triggerRef.current?.closest("[role='dialog']")}
          >
            <Primitive.Content
              asChild
              sideOffset={5}
              collisionPadding={10}
              position="popper"
              align={align}
            >
              <motion.div
                data-size={size}
                className={css.menu}
                initial={{
                  opacity: 0,
                  scale: 0.97,
                  y: 'calc(var(--direction) * 3px)',
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 'calc(var(--direction) * 0px)',
                }}
                exit={{
                  opacity: 0,
                  scale: 0.97,
                  y: 'calc(var(--direction) * 3px)',
                }}
                transition={{ duration: 0.2, ease: easeOutSmooth }}
              >
                <Primitive.ScrollUpButton className={css.scrollUp}>
                  <ChevronUp />
                </Primitive.ScrollUpButton>
                <Primitive.Viewport className={css.viewport}>
                  {children}
                </Primitive.Viewport>
                <Primitive.ScrollDownButton className={css.scrollDown}>
                  <ChevronDown />
                </Primitive.ScrollDownButton>
              </motion.div>
            </Primitive.Content>
          </Primitive.Portal>
        </AnimatePresence>
      </Primitive.Root>
    );
  }
) as <T extends string>(
  props: SelectProps<T> & { ref?: React.Ref<HTMLButtonElement> }
) => JSX.Element;

const SelectNamespace = Object.assign(Select, {
  Option: SelectOption,
  Label: SelectLabel,
  Group: Primitive.Group,
});

export { SelectNamespace as Select, SelectLabel, SelectOption };
